import { Anchor, Button, Input, Modal, Toast } from '@byecode/ui'
import { CopyButton, ErrorMessage, TagIcon, useAtomAction, useAtomData } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useAsyncFn } from 'react-use'

import { fetchOtherPlatformsAtom } from '@/atoms/application/action'
import { otherPlatformListAtom } from '@/atoms/application/state'
import {
    otherPlatformBackgroundColorMap,
    otherPlatformColorMap,
    otherPlatformDescribeMap,
    otherPlatformNameMap
} from '@/containers/OtherPlatforms/constant'
import { usePageURI } from '@/hooks/usePageURI'
import * as srv from '@/services'

import { PLACEHOLDER_PASSWORD } from '../constants'
import * as SC from '../styles'
import type { StripeAccountFormState } from './type'

interface StripeAccountProps {
    onClose?: () => void
}

const initData: StripeAccountFormState = {
    id: '',
    publicKey: '',
    secretKey: ''
}
const StripeAccount: React.FunctionComponent<StripeAccountProps> = ({ onClose }) => {
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)
    const { host } = usePageURI({ isProdHost: true })

    const account = useMemo(() => {
        const otherPlatform = find(item => item.type === 'STRIPE', otherPlatforms ?? [])
        return otherPlatform?.configurationComplete
            ? { ...otherPlatform?.config, id: otherPlatform.id, secretKey: PLACEHOLDER_PASSWORD, publicKey: PLACEHOLDER_PASSWORD }
            : { ...initData, id: otherPlatform?.id }
    }, [otherPlatforms])

    const webhooks = `https://${host}/byecode/external/stripe/pay/notify`

    const {
        control,
        register,
        handleSubmit,
        getValues,
        formState: { errors }
    } = useForm({
        mode: 'onSubmit',
        defaultValues: account
    })

    const [{ loading: submitLoading }, doUpdateIntegration] = useAsyncFn(srv.updateIntegration)

    const handleUpdate = useCallback(
        async (params: StripeAccountFormState) => {
            const { id, publicKey, secretKey } = params
            if (!id || submitLoading) {
                return
            }
            const res = await doUpdateIntegration({
                type: 'STRIPE',
                id,
                config: {
                    publicKey: publicKey === PLACEHOLDER_PASSWORD ? undefined : publicKey,
                    secretKey: secretKey === PLACEHOLDER_PASSWORD ? undefined : secretKey
                }
            })
            if (res) {
                fetchOtherPlatforms()
                onClose?.()
            }
        },
        [onClose, submitLoading, fetchOtherPlatforms, doUpdateIntegration]
    )

    const handleInviteSubmit = useCallback(
        (ev: React.FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleUpdate)()
        },
        [handleSubmit, handleUpdate]
    )

    return (
        <SC.Container onSubmit={handleInviteSubmit}>
            <SC.Content>
                <TagIcon
                    size={48}
                    iconSize={32}
                    icon="Stripe"
                    radius={6}
                    iconColor={otherPlatformColorMap['STRIPE']}
                    background={otherPlatformBackgroundColorMap['STRIPE']}
                />
                <SC.Title>{otherPlatformNameMap['STRIPE']}</SC.Title>
                <SC.Item>
                    <SC.Description>{otherPlatformDescribeMap['STRIPE']}</SC.Description>
                </SC.Item>
                <SC.Tip>
                    <SC.Icon color="var(--color-main)" type="WarningCircle" />
                    <SC.Text>如果您完善授权时遇到问题，请查看帮助文档：</SC.Text>
                    <Anchor
                        target="_blank"
                        style={{ fontWeight: 600, color: 'var(--color-main)' }}
                        to="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/lyr9sxw7nbigb0ds"
                    >
                        Stripe授权配置
                    </Anchor>
                </SC.Tip>
                <SC.Item>
                    <SC.Text>
                        公钥
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="publicKey" errors={errors}>
                        <Controller
                            name="publicKey"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        size="lg"
                                        autoComplete="new-password"
                                        type="password"
                                        placeholder="请填入 Stripe 账号的公钥"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        具有管理员权限登录{' '}
                        <Anchor
                            target="_blank"
                            to="https://dashboard.stripe.com/login?redirect=%2Faccount%2Fapikeys"
                            style={{ color: 'var(--color-main)' }}
                        >
                            Stripe管理平台
                        </Anchor>{' '}
                        [首页] {'>'} [公钥] {'>'} [点击以复制]
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        API 密钥
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="secretKey" errors={errors}>
                        <Controller
                            name="secretKey"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        size="lg"
                                        autoComplete="new-password"
                                        type="password"
                                        placeholder="请填入API 密钥"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        具有管理员权限登录{' '}
                        <Anchor
                            target="_blank"
                            to="https://dashboard.stripe.com/login?redirect=%2Faccount%2Fapikeys"
                            style={{ color: 'var(--color-main)' }}
                        >
                            Stripe管理平台
                        </Anchor>{' '}
                        {'>'} [首页] {'>'} [API 密钥] {'>'} [显示一次] {'>'} [点击以复制]
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>回调函数 Webhook</SC.Text>
                    <Input placeholder={webhooks} disabled size="lg" suffix={<CopyButton style={{ marginRight: 8 }} value={webhooks} />} />
                    <SC.Description>
                        请复制该链接粘贴到管理平台到：{' '}
                        <Anchor
                            target="_blank"
                            to="https://dashboard.stripe.com/login?redirect=%2Faccount%2Fapikeys"
                            style={{ color: 'var(--color-main)', fontWeight: 600 }}
                        >
                            Stripe管理平台
                        </Anchor>{' '}
                        {'>'} [首页] {'>'} [开发人员] {'>'} [Webhook] {'>'} [添加托管端点] {'>'} [复制当前Webhook链接至端点 URL] {'>'}{' '}
                        添加端点，否则集成将不会生效
                    </SC.Description>
                </SC.Item>
            </SC.Content>
            <SC.Footer>
                <SC.LeftFill />
                <SC.RightFill>
                    <Button size="lg" style={{ width: 104, height: 40 }} onClick={() => onClose?.()}>
                        取消
                    </Button>
                    <Button size="lg" loading={submitLoading} style={{ width: 104, height: 40 }} type="primary" htmlType="submit">
                        确定
                    </Button>
                </SC.RightFill>
            </SC.Footer>
        </SC.Container>
    )
}

export default StripeAccount
